/**
 * GOP layout for the Workspace grid.
 *
 * The Workspace's default layout is the two-section income statement: every
 * revenue department, every expense department, Net Income. The GOP layout
 * regroups the SAME department rows into the USALI summary operating
 * statement the Financial report prints:
 *
 *   Revenue                          operated departments' revenue
 *   Department Expense               operated departments' expense
 *   Departmental Profit
 *   Undistributed Expenses
 *   Gross Operating Profit (GOP)
 *   Management Fees                  ┐
 *   Income Before Fixed Charges      │ each band and the profit line under
 *   Fixed Charges                    │ it appear only when the chart tags
 *   EBITDA                           │ something into that band
 *   Interest, Depreciation & Other   ┘
 *   Net Income
 *
 * Nothing about a row changes — same DepartmentRow, same AccountRow, same
 * editing, same drivers, same expanded columns — only which band it sits
 * under and which subtotals run beneath it.
 *
 * WHICH BAND A DEPARTMENT LANDS IN is decided by the Financial report's own
 * classifier (`buildUsaliUnits` in utils/financialPnlMatrix.js): explicit
 * `chartOfAccountDepartments.usaliSection` first, then a department NAMED
 * non-operating / fixed charges, then "has revenue" ⇒ Operated, else
 * Undistributed — with expense departments paired to their revenue
 * department by stripped-name stem ("Rooms Expense" ↔ "Rooms Revenue") or by
 * an explicit revenue-basis link. Running the report's code, rather than a
 * copy of its rules, is what keeps this grid's GOP equal to the report's.
 */

import {
  BELOW_GOP_SECTIONS,
  USALI_PRECEDENCE,
  USALI_SECTIONS,
  buildLinkIndex,
  buildUsaliUnits,
} from '../../../utils/financialPnlMatrix';
import type { ForecastAccount } from '../types';

/** One department row as the COA departments endpoint returns it. */
export interface DepartmentMetaRow {
  id: number;
  name?: string | null;
  usaliSection?: string | null;
  presentationName?: string | null;
  displayOrder?: number | null;
  locationDisplayOrder?: number | null;
}

/** One `% Rev` / GOP pairing link as the mappings endpoint returns it. */
export interface RevenueBasisLinkRow {
  chartOfAccountDepartmentId?: number | null;
  chartOfAccountId?: number | null;
  revenueChartOfAccountId?: number | null;
  revenueAccountNumber?: string | null;
}

export type GridLayout = 'pnl' | 'gop';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** Same normalisation PnLGrid groups departments by. */
export const normalizeDeptName = (s: string): string => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();

export interface ClassifyDepartmentsParams {
  accounts: ForecastAccount[];
  departments?: DepartmentMetaRow[];
  revenueBasisLinks?: RevenueBasisLinkRow[];
  locationId: number;
}

/**
 * Department name (normalised) → USALI section, decided by the report's unit
 * classifier over this version's accounts. Amounts play no part in the
 * decision, so every account contributes a single zero-valued leaf.
 */
export function classifyDepartmentSections({
  accounts,
  departments = [],
  revenueBasisLinks = [],
  locationId,
}: ClassifyDepartmentsParams): Map<string, string> {
  const rows = (accounts || []).map((a) => ({
    locationId,
    locationName: '',
    month: '2000-01-01',
    accountId: a.chartOfAccountId,
    accountName: a.accountName,
    accountNumber: a.accountNumber,
    accountType: a.accountType,
    department: a.department,
    chartOfAccountDepartmentId: a.departmentId ?? null,
    actual: 0,
    budget: 0,
    variance: 0,
  }));
  const usaliSections = (departments || [])
    .filter((d) => d && (d.usaliSection || d.presentationName))
    .map((d) => ({
      chartOfAccountDepartmentId: d.id,
      name: d.name ?? null,
      usaliSection: d.usaliSection ?? null,
      presentationName: d.presentationName ?? null,
    }));
  const payload = { rows, usaliSections, revenueBasisLinks: revenueBasisLinks || [] };

  const units = buildUsaliUnits(payload, buildLinkIndex(payload)).get(locationId) || new Map<string, any>();
  const rank = (section: string) => {
    const i = USALI_PRECEDENCE.indexOf(section);
    return i === -1 ? USALI_PRECEDENCE.length : i;
  };

  // A department normally sits in one unit. When account-level links split it
  // across two, the section earlier in USALI precedence wins — the same
  // tie-break the report applies inside a unit.
  const bySection = new Map<string, string>();
  units.forEach((unit: any) => {
    (unit.rows || []).forEach((r: any) => {
      const key = normalizeDeptName(r.department);
      if (!key) return;
      const current = bySection.get(key);
      if (!current || rank(unit.section) < rank(current)) bySection.set(key, unit.section);
    });
  });
  return bySection;
}

export interface GopDeptRef<T> {
  dept: T;
  isExpense: boolean;
}

export interface GopCostBand<T> {
  section: string;
  /** Stable key for the band's expand state (`gop:<key>`). */
  key: string;
  label: string;
  members: GopDeptRef<T>[];
}

export interface GopLayout<T> {
  /** Operated departments' revenue rows. */
  revenue: T[];
  /** Operated departments' expense rows. */
  deptExpense: T[];
  /** Cost bands in statement order, empty ones dropped. */
  costBands: GopCostBand<T>[];
  /** True when any band sits below the GOP line (Net Income differs from GOP). */
  hasBelowGop: boolean;
}

const COST_BANDS: Array<{ section: string; key: string; label: string }> = [
  { section: USALI_SECTIONS.UNDISTRIBUTED, key: 'UND', label: 'Undistributed Expenses' },
  { section: USALI_SECTIONS.MANAGEMENT_FEE, key: 'MGMT', label: 'Management Fees' },
  { section: USALI_SECTIONS.NON_OPERATING, key: 'FIXED', label: 'Fixed Charges' },
  { section: USALI_SECTIONS.BELOW_EBITDA, key: 'BELOW', label: 'Interest, Depreciation & Other' },
];

/**
 * Deal the grid's revenue and expense department groups into statement bands.
 * Department order inside each band is the order the groups arrived in — the
 * drag-set P&L Mapping order the grid already applies.
 *
 * Fallbacks when the classifier has no answer for a department (it always
 * does for one it saw, so this is belt-and-braces): revenue → Operated,
 * expense → Undistributed, mirroring the classifier's own inference.
 */
export function buildGopLayout<T extends { department: string }>(
  revenueDepts: T[],
  expenseDepts: T[],
  sectionByDept: Map<string, string>
): GopLayout<T> {
  const sectionFor = (dept: T, isExpense: boolean) =>
    sectionByDept.get(normalizeDeptName(dept.department)) ||
    (isExpense ? USALI_SECTIONS.UNDISTRIBUTED : USALI_SECTIONS.OPERATED);

  const revenue: T[] = [];
  const deptExpense: T[] = [];
  const members = new Map<string, GopDeptRef<T>[]>();
  COST_BANDS.forEach((b) => members.set(b.section, []));

  revenueDepts.forEach((dept) => {
    const section = sectionFor(dept, false);
    if (section === USALI_SECTIONS.OPERATED) revenue.push(dept);
    else members.get(section)?.push({ dept, isExpense: false });
  });
  expenseDepts.forEach((dept) => {
    const section = sectionFor(dept, true);
    if (section === USALI_SECTIONS.OPERATED) deptExpense.push(dept);
    else members.get(section)?.push({ dept, isExpense: true });
  });

  const costBands = COST_BANDS
    .map((b) => ({ ...b, members: members.get(b.section) || [] }))
    .filter((b) => b.members.length > 0);

  return {
    revenue,
    deptExpense,
    costBands,
    hasBelowGop: costBands.some((b) => BELOW_GOP_SECTIONS.includes(b.section)),
  };
}

/**
 * Field-wise sum of department expanded totals across the income and expense
 * sides of a statement line. Every stored field already carries its own sign
 * convention (actual/lyActual in transaction sign, budget expense-negative),
 * so a plain sum IS the line's value in that same convention — the column
 * renderers then present it through `isExpense` exactly as they would a
 * single department. Only `forecast` is display-positive on both sides, so
 * it is the one field that subtracts.
 */
export interface StatementTotals {
  forecast: number;
  budget: number;
  actual: number;
  lyActual: number;
  lyBudget?: number;
  lyBudgetOccupiedRooms?: number;
}

export interface StatementDept {
  monthlyTotals: Record<string, number>;
  monthlyExpandedTotals: Record<string, StatementTotals>;
}

export interface StatementLine {
  monthly: Record<string, number>;
  total: number;
  expanded: Record<string, StatementTotals>;
}

export function sumStatementLine(income: StatementDept[], expense: StatementDept[], costPositive: boolean): StatementLine {
  const monthly: Record<string, number> = {};
  const expanded: Record<string, StatementTotals> = {};
  const sign = costPositive ? -1 : 1;
  let total = 0;
  for (const month of MONTH_KEYS) {
    let value = 0;
    const e: StatementTotals = { forecast: 0, budget: 0, actual: 0, lyActual: 0, lyBudget: 0, lyBudgetOccupiedRooms: 0 };
    const fold = (dept: StatementDept, side: 1 | -1) => {
      value += side * (dept.monthlyTotals[month] || 0);
      const x = dept.monthlyExpandedTotals[month];
      if (!x) return;
      e.forecast += side * (x.forecast || 0);
      e.budget += x.budget || 0;
      e.actual += x.actual || 0;
      e.lyActual += x.lyActual || 0;
      e.lyBudget! += x.lyBudget || 0;
      e.lyBudgetOccupiedRooms = x.lyBudgetOccupiedRooms || e.lyBudgetOccupiedRooms;
    };
    income.forEach((d) => fold(d, 1));
    expense.forEach((d) => fold(d, -1));
    monthly[month] = sign * value;
    e.forecast = sign * e.forecast;
    expanded[month] = e;
    total += monthly[month];
  }
  return { monthly, total, expanded };
}
